import axios from 'axios';
import { clearSessionStorage } from '../utils/sessionLogout';

import {
    USER_LOGIN_REQUEST,
    USER_LOGIN_SUCCESS,
    USER_LOGIN_FAIL,

    USER_LOGOUT,

    USER_REGISTER_REQUEST,
    USER_REGISTER_SUCCESS,
    USER_REGISTER_FAIL,

    USER_DETAILS_REQUEST,
    USER_DETAILS_SUCCESS,
    USER_DETAILS_FAIL,
    USER_DETAILS_RESET,

    IDENTITY_USER_DETAILS_REQUEST,
    IDENTITY_USER_DETAILS_SUCCESS,
    IDENTITY_USER_DETAILS_FAIL,
    IDENTITY_USER_DETAILS_RESET,

    IDENTITY_USER_UPDATE_REQUEST,
    IDENTITY_USER_UPDATE_SUCCESS,
    IDENTITY_USER_UPDATE_FAIL,
    IDENTITY_USER_UPDATE_RESET,

    IDENTITY_USER_CREATE_REQUEST,
    IDENTITY_USER_CREATE_SUCCESS,
    IDENTITY_USER_CREATE_FAIL,
    IDENTITY_USER_CREATE_RESET,

    USER_LIST_REQUEST,
    USER_LIST_SUCCESS,
    USER_LIST_FAIL,
    USER_LIST_RESET,

    USER_DELETE_REQUEST,
    USER_DELETE_SUCCESS,
    USER_DELETE_FAIL,

    USER_UPDATE_REQUEST,
    USER_UPDATE_SUCCESS,
    USER_UPDATE_FAIL,

    AUTHENTICATED_REQUEST,
    AUTHENTICATED_SUCCESS,
    AUTHENTICATED_FAIL,

    UPDATE_USER_PASSWORD_REQUEST,
    UPDATE_USER_PASSWORD_SUCCESS,
    UPDATE_USER_PASSWORD_FAIL,

    GET_POLICIES_REQUEST,
    GET_POLICIES_SUCCESS,
    GET_POLICIES_FAIL,

    GET_ALL_ROLES_REQUEST,
    GET_ALL_ROLES_SUCCESS,
    GET_ALL_ROLES_FAIL,

    UPDATE_ROLE_REQUEST,
    UPDATE_ROLE_SUCCESS,
    UPDATE_ROLE_FAIL,

    CREATE_ROLE_REQUEST,
    CREATE_ROLE_SUCCESS,
    CREATE_ROLE_FAIL,

    DELETE_ROLE_REQUEST,
    DELETE_ROLE_SUCCESS,
    DELETE_ROLE_FAIL,

    UPDATE_ROLE_POLICIES_REQUEST,
    UPDATE_ROLE_POLICIES_SUCCESS,
    UPDATE_ROLE_POLICIES_FAIL,

    GET_ROLE_POLICIES_REQUEST,
    GET_ROLE_POLICIES_SUCCESS,
    GET_ROLE_POLICIES_FAIL,

    GET_USER_PERMISSIONS_REQUEST,
    GET_USER_PERMISSIONS_SUCCESS,
    GET_USER_PERMISSIONS_FAIL,
    EXPORT_ROLES_POLICIES_REQUEST,
    EXPORT_ROLES_POLICIES_SUCCESS,
    EXPORT_ROLES_POLICIES_FAIL,
    IMPORT_ROLES_POLICIES_REQUEST,
    IMPORT_ROLES_POLICIES_SUCCESS,
    IMPORT_ROLES_POLICIES_FAIL,

} from '../constants/userConstants'

// Helper functions
const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/user';
const AUTH_BASE_URL = '/dotnet_api/v1/auth';

// Authentication actions
export const login = (email, password) => async (dispatch) => {
    try {
        dispatch({ type: USER_LOGIN_REQUEST });
        
        const { data } = await axios.post(
            `${AUTH_BASE_URL}/signin/`,
            { email, password },
            { headers: { 'Content-type': 'application/json' } }
        );

        if (data.requiresTOTP) {
            dispatch({ type: USER_LOGIN_SUCCESS, payload: { ...data, requiresTOTP: true } });
            localStorage.setItem('userInfo', JSON.stringify({ email, requiresTOTP: true }));
        } else {
            // Fetch effective permissions before persisting so userInfo.policies is
            // always populated from the first login, regardless of TOTP flow.
            if (data.token) {
                try {
                    const permRes = await axios.get(`${AUTH_BASE_URL}/permissions`, {
                        headers: { 'Content-type': 'application/json', Authorization: `Bearer ${data.token}` },
                    });
                    data.policies = permRes.data.permissions || [];
                } catch {
                    // Non-fatal — permissions will be fetched again on Layout mount.
                }
            }
            dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
            localStorage.setItem('userInfo', JSON.stringify(data));
            localStorage.setItem('2fauthenticated', JSON.stringify(false));
        }
    } catch (error) {
        dispatch({
            type: USER_LOGIN_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const verifyLoginTOTP = (email, code) => async (dispatch) => {
    try {
        dispatch({ type: USER_LOGIN_REQUEST });
        
        const { data } = await axios.post(
            `${AUTH_BASE_URL}/verify-login-totp/`,
            { email, code },
            { headers: { 'Content-type': 'application/json' } }
        );

        dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
        localStorage.setItem('userInfo', JSON.stringify(data));
        localStorage.setItem('2fauthenticated', JSON.stringify(true));
        
        // Fetch user permissions after successful TOTP verification
        await dispatch(getUserPermissions());
    } catch (error) {
        dispatch({
            type: USER_LOGIN_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const logout = () => (dispatch) => {
    clearSessionStorage();
    dispatch({ type: USER_LOGOUT });
    dispatch({ type: USER_DETAILS_RESET });
    dispatch({ type: USER_LIST_RESET });
};

export const register = (email, password, businessOrganisationId) => async (dispatch) => {
    try {
        dispatch({ type: USER_REGISTER_REQUEST });
        
        const request = {
            email,
            userName: email,
            firstName: email,
            lastName: email,
            password,
            businessOrganisationId
        };

        const { data } = await axios.post(
            `${AUTH_BASE_URL}/signup/`,
            request,
            { headers: { 'Content-type': 'application/json' } }
        );

        dispatch({ type: USER_REGISTER_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: USER_REGISTER_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

// Identity user actions
export const createIdentityUser = (identityUser) => async (dispatch) => {
    try {
        dispatch({ type: IDENTITY_USER_CREATE_REQUEST });
        
        const request = {
            email: identityUser.email,
            password: identityUser.password,
            businessOrganisationId: identityUser.businessOrganisationId,
            userName: identityUser.userName,
            firstName: identityUser.firstName,
            lastName: identityUser.lastName,
        };

        const { data } = await axios.post(
            `${AUTH_BASE_URL}/signup/`,
            request,
            { headers: { 'Content-type': 'application/json' } }
        );

        dispatch({ type: IDENTITY_USER_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: IDENTITY_USER_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getIdentityUserDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: IDENTITY_USER_DETAILS_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: IDENTITY_USER_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: IDENTITY_USER_DETAILS_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const updateIdentityUser = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: IDENTITY_USER_UPDATE_REQUEST });
        const { data } = await axios.put(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: IDENTITY_USER_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: IDENTITY_USER_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

// User management actions
export const listUsers = (filter = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_LIST_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const isSuperUser = userInfo?.roles?.includes('SuperUser');
        const config = {
            ...getAuthConfig(userInfo.token),
            params: filter,
        };

        let data;

        if (isSuperUser) {
            // SuperUsers can see all users across organisations
            ({ data } = await axios.get(`${API_BASE_URL}/all`, config));
        } else {
            // Non-SuperUsers are restricted to their organisation
            ({ data } = await axios.get(
                `${API_BASE_URL}/organisation/${userInfo.businessOrganisationId}/all`,
                config
            ));
        }

        // If data is an array, wrap it in an object
        const payload = Array.isArray(data)
            ? { users: data, currentPage: 1, totalPages: 1 }
            : data;

        dispatch({ type: USER_LIST_SUCCESS, payload });
    } catch (error) {
        dispatch({
            type: USER_LIST_FAIL,
            payload: formatErrorMessage(error)[0],
        });
    }
};

// List all users in an organisation
export const listUsersInOrganisation = () => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_LIST_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.get(
            `${API_BASE_URL}/organisation/${userInfo.businessOrganisationId}/all`,
            getAuthConfig(userInfo.token)
        );

        dispatch({ type: USER_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: USER_LIST_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const deleteUser = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DELETE_REQUEST });
        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: USER_DELETE_SUCCESS });
    } catch (error) {
        dispatch({
            type: USER_DELETE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const updateUser = (changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_UPDATE_REQUEST });
        const { data } = await axios.put(
            `${API_BASE_URL}/${changes.id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: USER_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: USER_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

// Role and policy management
export const getPolicies = () => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_POLICIES_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/policies`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: GET_POLICIES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: GET_POLICIES_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const getAllRoles = () => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_ALL_ROLES_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/roles`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: GET_ALL_ROLES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: GET_ALL_ROLES_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const updateRole = (roleName, newRoleName) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_ROLE_REQUEST });
        const { data } = await axios.put(
            `${API_BASE_URL}/roles/update/${roleName}`,
            JSON.stringify(newRoleName),
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: UPDATE_ROLE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: UPDATE_ROLE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const createRole = (roleName) => async (dispatch, getState) => {
    try {
        dispatch({ type: CREATE_ROLE_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        // Ensure Content-Type is set
        config.headers['Content-Type'] = 'application/json';
        const { data } = await axios.post(
            `${API_BASE_URL}/roles`,
            roleName, // Send as raw string
            config
        );
        dispatch({ type: CREATE_ROLE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: CREATE_ROLE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const deleteRole = (roleName) => async (dispatch, getState) => {
    try {
        dispatch({ type: DELETE_ROLE_REQUEST });
        await axios.delete(
            `${API_BASE_URL}/roles/${roleName}/claims`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: DELETE_ROLE_SUCCESS });
    } catch (error) {
        dispatch({
            type: DELETE_ROLE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const updateRolePolicies = (roleName, policies) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_ROLE_POLICIES_REQUEST });
        
        // Ensure we have a flat array of strings
        let policyArray;
        if (Array.isArray(policies)) {
            // If policies is an array of objects with a policies property, flatten it
            if (policies.length > 0 && policies[0].policies) {
                policyArray = policies[0].policies;
            } else {
                policyArray = policies;
            }
        } else if (policies.policies) {
            // If policies is an object with a policies property
            policyArray = policies.policies;
        } else {
            policyArray = [policies]; // Single policy case
        }

        const payload = { policies: policyArray };
        
        const { data } = await axios.patch(
            `${API_BASE_URL}/roles/${roleName}/policies`,
            payload,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: UPDATE_ROLE_POLICIES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: UPDATE_ROLE_POLICIES_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const getRolePolicies = (roleName) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_ROLE_POLICIES_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/roles/${roleName}/policies`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: GET_ROLE_POLICIES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: GET_ROLE_POLICIES_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

// Get current user's effective permissions
// Get current user permissions
export const getUserPermissions = () => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_USER_PERMISSIONS_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = getAuthConfig(userInfo.token);

        const { data } = await axios.get('/dotnet_api/v1/auth/permissions', config);

        const permissions = data.permissions || [];

        dispatch({
            type: GET_USER_PERMISSIONS_SUCCESS,
            payload: permissions,
        });

        // Update localStorage with fresh permissions
        const updatedUserInfo = {
            ...userInfo,
            policies: permissions
        };
        localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));

        return permissions;
    } catch (error) {
        dispatch({
            type: GET_USER_PERMISSIONS_FAIL,
            payload: formatErrorMessage(error)[0],
        });
        return [];
    }
};

// Password Reset
export const resetPassword = (id, newPassword) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_USER_PASSWORD_REQUEST });
        
        const { data } = await axios.post(
            `${API_BASE_URL}/${id}/updatepassword`,
            { NewPassword: newPassword },
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        
        dispatch({ type: UPDATE_USER_PASSWORD_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: UPDATE_USER_PASSWORD_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const exportRolesAndPolicies = () => async (dispatch, getState) => {
    try {
        dispatch({ type: EXPORT_ROLES_POLICIES_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get('/dotnet_api/v1/user/roles/export', config);
        dispatch({ type: EXPORT_ROLES_POLICIES_SUCCESS, payload: data });
        return data;
    } catch (error) {
        dispatch({ type: EXPORT_ROLES_POLICIES_FAIL, payload: formatErrorMessage(error)[0] });
        throw error;
    }
};

export const importRolesAndPolicies = (importData, mode = 'Merge') => async (dispatch, getState) => {
    try {
        dispatch({ type: IMPORT_ROLES_POLICIES_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const payload = {
            mode,
            roles: importData.roles || importData.Roles || [],
        };
        const { data } = await axios.post('/dotnet_api/v1/user/roles/import', payload, config);
        dispatch({ type: IMPORT_ROLES_POLICIES_SUCCESS, payload: data });
        return data;
    } catch (error) {
        dispatch({ type: IMPORT_ROLES_POLICIES_FAIL, payload: formatErrorMessage(error)[0] });
        throw error;
    }
};